import type { FunctionComponent, ReactElement } from "react";
import { useCallback, useState } from "react";
import { useRootStore } from "../../stores/hooks";
import {
  getTimezoneOffset,
  getCurrentTime,
  getTimezoneShortLabel,
  timezones,
} from "../../timezone/timezones";
import * as S from "./style";
import { t } from "i18next";
import {
  Text,
  EmptyButton,
  Icon,
  KeyValueCustomSelect,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  RoundButton,
  roundButtonStylesMedium,
} from "@smartsuite/react-ui/lib";
import { CheckmarkOption } from "@smartsuite/react-ui/lib/components/selects/components/CheckmarkOption/CheckmarkOption";

interface ModalProps {
  onClose: () => void;
  onSave: (timezone: string) => void;
}

export const TimeZoneModal: FunctionComponent<ModalProps> = ({
  onClose,
  onSave,
}) => {
  const { automations, config } = useRootStore();
  const [value, setValue] = useState(automations.candidate.self.timezone);
  const [isDropdownOpened, setIsDropdownOpened] = useState(false);

  const handleOnChange = useCallback((value) => {
    setValue(value);
  }, []);

  const getTimezoneLabel = (value: string): JSX.Element | undefined => {
    const timezone = timezones.find((timezone) => timezone.value === value);
    if (timezone !== undefined) {
      return renderOptionText(
        timezone.shortLabel,
        `(${getTimezoneOffset(timezone.value)})`,
        timezone.label,
      );
    }
  };

  return (
    <ModalOverlay animation="fade" active={true} closeByEsc={true}>
      <S.Container>
        <Modal
          onClose={onClose}
          size="SM"
          withoutHat={true}
          header={
            <>
              <ModalHeader title={t("timezone.change-timezone")} />
              <S.Close>
                <Icon
                  name="close"
                  color="currentColor"
                  onClick={onClose}
                  transition
                />
              </S.Close>
            </>
          }
          footer={
            <ModalFooter
              buttons={
                <>
                  <EmptyButton
                    size="lg"
                    onClick={onClose}
                    style={{ marginRight: 10 }}
                  >
                    {t("timezone.cancel")}
                  </EmptyButton>
                  <RoundButton
                    styles={roundButtonStylesMedium}
                    onClick={() => onSave(value ?? "")}
                  >
                    {t("timezone.save")}
                  </RoundButton>
                </>
              }
            />
          }
        >
          <S.Body>
            <S.Description>
              <Text color="tertiary">
                {t("timezone.change-time")}{" "}
                <Text tag="span" weight="medium" color="primary">
                  {t("timezone.this-automation-only")}
                </Text>
              </Text>
            </S.Description>

            <S.Label>
              <Text weight="medium">{t("timezone.timezone")}</Text>
            </S.Label>

            <KeyValueCustomSelect
              my="left top+2"
              state={isDropdownOpened ? "active" : "default"}
              disabled={false}
              showClear={false}
              value={value}
              options={timezones.map((timezone) => ({
                label: `${timezone.shortLabel} (${getTimezoneOffset(
                  timezone.value,
                )}) ${timezone.label}`,
                value: timezone.value,
              }))}
              showFilterGreaterThan={5}
              searchPlaceholder="Search"
              searchIcon={false}
              renderSelectedOption={(item) => getTimezoneLabel(item.value)}
              renderOption={(item) => (
                <S.DropdownOption>
                  <CheckmarkOption
                    rightAlignSelected={true}
                    selected={item.value === value}
                  >
                    {getTimezoneLabel(item.value)}
                  </CheckmarkOption>
                </S.DropdownOption>
              )}
              renderDropdownFooter={() => (
                <S.DropdownFooter>
                  <Text size="s" color="tertiary">
                    <Text tag="span" color="primary">
                      {t("timezone.workspace-timezone")}
                    </Text>{" "}
                    is {getTimezoneShortLabel(config.workspace.timezone)} (
                    {getCurrentTime(
                      config.workspace.timezone,
                      config.userPreferences,
                    )}
                    )
                  </Text>
                </S.DropdownFooter>
              )}
              placeholderArrowStyle="filled-dark"
              onOpenedChange={setIsDropdownOpened}
              onChange={handleOnChange}
            />
          </S.Body>
        </Modal>
      </S.Container>
    </ModalOverlay>
  );
};

const renderOptionText = (
  label: string,
  offsetLabel: string,
  description: string,
): ReactElement => {
  return (
    <Text wrapMode="ellipsis" color="secondary">
      <Text weight="medium" color="primary">
        {label}
      </Text>
      {" "}
      {offsetLabel}
      {" "}
      {t(description)}
    </Text>
  );
};
